import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { UserStatsCards } from '@/components/admin/users/UserStatsCards';
import { UserFilters } from '@/components/admin/users/UserFilters';
import { UserManagementTable } from '@/components/admin/users/UserManagementTable';
import { UserDetailModal } from '@/components/admin/users/UserDetailModal';
import { 
  userManagementService, 
  UserFilters as UserFiltersType, 
  UserData 
} from '@/services/admin/user-management-service';
import { useDebounce } from '@/hooks/use-debounce';

const UserManagementPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState<UserFiltersType>({});
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [isUserDetailOpen, setIsUserDetailOpen] = useState(false);
  
  const queryClient = useQueryClient();
  
  // Debounce filters to avoid too many API calls
  const debouncedFilters = useDebounce(filters, 500);

  // Fetch users with filters and pagination
  const { 
    data: usersData, 
    isLoading: isLoadingUsers,
    refetch: refetchUsers 
  } = useQuery({
    queryKey: ['admin-users', currentPage, pageSize, debouncedFilters],
    queryFn: () => userManagementService.getUsers(currentPage, pageSize, debouncedFilters),
    placeholderData: (previousData) => previousData,
  });

  const users = usersData?.users || [];
  const totalCount = usersData?.totalCount || 0;

  // Reset to first page when filters change
  React.useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [debouncedFilters]);

  const handleFiltersChange = (newFilters: UserFiltersType) => {
    setFilters(newFilters);
  };

  const handleExport = async () => {
    try {
      toast.loading('Exporting users...');
      const blob = await userManagementService.exportUsers(debouncedFilters);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Users exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export users');
    }
  };

  const handleRefresh = () => {
    refetchUsers();
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
    toast.success('Data refreshed');
  };

  const handleViewUser = (user: UserData) => {
    setSelectedUser(user);
    setIsUserDetailOpen(true);
  };

  const handleEditUser = (user: UserData) => {
    refetchUsers();
    queryClient.invalidateQueries({ queryKey: ['user-stats'] });
  };

  const handleToggleUserStatus = async (user: UserData) => {
    try {
      const newStatus = !user.isActive;
      await userManagementService.updateUserStatus(user.id, newStatus);
      
      // Refresh data
      refetchUsers();
    queryClient.invalidateQueries({ queryKey: ['user-stats'] });
      
      toast.success(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Toggle status error:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async (user: UserData) => {
    if (!confirm(`Are you sure you want to delete ${user.name}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await userManagementService.deleteUser(user.id);
      
      // Refresh data
      refetchUsers();
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
      
      toast.success('User deleted successfully');
    } catch (error) {
      console.error('Delete user error:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleBulkAction = async (action: string, selectedUsers: UserData[]) => {
    if (selectedUsers.length === 0) return;

    try {
      if (action === 'activate') {
        await Promise.all(selectedUsers.map(user => 
          userManagementService.updateUserStatus(user.id, true)
        ));
        toast.success(`${selectedUsers.length} users activated successfully`);
      } else if (action === 'deactivate') {
        await Promise.all(selectedUsers.map(user => 
          userManagementService.updateUserStatus(user.id, false)
        ));
        toast.success(`${selectedUsers.length} users deactivated successfully`);
      } else if (action === 'delete') {
        if (!confirm(`Are you sure you want to delete ${selectedUsers.length} users? This action cannot be undone.`)) {
          return;
        }
        await Promise.all(selectedUsers.map(user => 
          userManagementService.deleteUser(user.id)
        ));
        toast.success(`${selectedUsers.length} users deleted successfully`);
      } else if (action === 'export') {
        // For bulk export, just export selected users
        const blob = await userManagementService.exportUsers(debouncedFilters);
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `selected-users-export-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('Selected users exported successfully!');
      } else if (action.startsWith('subscription:')) {
        const plan = action.split(':')[1];
        await Promise.all(selectedUsers.map(user => 
          userManagementService.updateUserProfile(user.id, { 
            subscriptionPlan: plan,
            subscriptionStatus: 'active'
          })
        ));
        toast.success(`${selectedUsers.length} users updated to ${plan} plan`);
      }

      // Refresh data
      refetchUsers();
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
    } catch (error) {
      console.error('Bulk action error:', error);
      toast.error(`Failed to perform bulk action: ${action}`);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">User Management</h1>
        <p className="text-muted-foreground">
          Manage users, subscriptions, and monitor activity across the platform
        </p>
      </div>

      {/* Stats Cards */}
      <UserStatsCards />

      {/* Main Content */}
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>
            All registered users with their subscription and activity information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <UserFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onExport={handleExport}
            onRefresh={handleRefresh}
            isLoading={isLoadingUsers}
          />

          {/* Users Table */}
          <UserManagementTable
            users={users}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            isLoading={isLoadingUsers}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onViewUser={handleViewUser}
            onEditUser={handleEditUser}
            onToggleUserStatus={handleToggleUserStatus}
            onDeleteUser={handleDeleteUser}
            onBulkAction={handleBulkAction}
          />
        </CardContent>
      </Card>

      {/* User Detail Modal */}
      <UserDetailModal
        user={selectedUser}
        isOpen={isUserDetailOpen}
        onClose={() => {
          setIsUserDetailOpen(false);
          setSelectedUser(null);
        }}
        onEditUser={handleEditUser}
        onToggleUserStatus={handleToggleUserStatus}
        onDeleteUser={handleDeleteUser}
      />
    </div>
  );
};

export default UserManagementPage;